import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState, useMemo, useCallback } from "react";
import { InferType, object, string, number, boolean } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Feature, MultiPolygon, MultiLineString } from "geojson";
import { PlotType } from "@/types/enums";
import { AbLine } from "@/types/abLine";
import { BlockPlotFeature } from "@/types/plot";
import { Button } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faRotateLeft,
    faRotateRight,
    faArrowsLeftRight,
    faArrowsUpDown,
    faShuffle,
} from "@fortawesome/free-solid-svg-icons";
import SelectButtonInput from "../inputs/SelectButtonInput";
import NumberInput from "../inputs/NumberInput";
import FileInput from "../inputs/FileInput";
import AutoCompleteInput from "../inputs/AutoCompleteInput";
import * as turf from "@turf/turf";
import useRequest from "@/lib/request/useRequest";
import useMap from "@/lib/map/useMap";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useAuth from "@/lib/auth/useAuth";
import { MapMouseEvent } from "mapbox-gl";
import {
    addUpdateGeojsonSource,
    addUpdateGeojsonLayers,
    removeLayersIfExists,
    removeSourcesIfExists,
    updateLayerVisibility,
    moveLayersToTop,
} from "@/lib/utils/map";
import {
    AB_LINE_SOURCE,
    AB_LINE_LAYER,
    AB_POINTS_LAYER,
    PLOT_GRID_SOURCE,
    PLOT_GRID_LAYER,
    PLOT_HIGHLIGHT_SOURCE,
    PLOT_HIGHLIGHT_FILL_LAYER,
    PLOT_HIGHLIGHT_LINE_LAYER,
} from "@/lib/map/layers";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import useFeatureFlags from "@/lib/featureFlags/useFeatureFlags";

const schema = object().shape({
    userIsSentinel: boolean().required(),
    canary_offset: number()
        .required()
        .default(-30)
        .label("Canary Offset")
        .when("userIsSentinel", {
            is: (val: boolean) => val,
            then: (schema) => schema.max(0),
            otherwise: (schema) => schema.max(-30),
        }),
    reference_offset: number()
        .required()
        .default(60)
        .label("Reference Offset")
        .when("userIsSentinel", {
            is: (val: boolean) => val,
            then: (schema) => schema.min(0),
            otherwise: (schema) => schema.min(30),
        }),
    orientation: string()
        .required()
        .oneOf(["heading", "ab-line"])
        .default("heading")
        .label("Orientation"),
    ab_line: number().nullable().label("AB Line"),
    heading: number().required().min(0).max(360).default(0).label("Heading"),
    implement_width_feet: number().required().default(40).label("Pass Width"),
    pass_count: number().required().default(3).label("Passes"),
    length_feet: number().required().default(240).label("Length"),
    pair_layout: string()
        .required()
        .oneOf(["side-by-side", "end-to-end"])
        .default("side-by-side")
        .label("Pair Layout"),
    canary_geometry: object().required(),
    reference_geometry: object().required(),
    canary_id: number().nullable(),
    reference_id: number().nullable(),
});
export type BlockForm = InferType<typeof schema>;

function getBlockHeading(block: Feature) {
    if (!block.geometry) {
        return 0;
    }
    const geometry = block.geometry as MultiPolygon;
    const bearing = turf.bearing(
        turf.point(geometry.coordinates[0][0][0]),
        turf.point(geometry.coordinates[0][0][1]),
    );
    return Math.round(turf.bearingToAzimuth(bearing)) % 360;
}

function BlockPlotForm({
    fieldId,
    placement,
    plots,
    onFormChange,
}: {
    fieldId: string | number;
    placement: "manual" | "automatic";
    plots?: Feature[];
    onFormChange?: (form: BlockForm | BlockPlotFeature[]) => void;
}) {
    const { formatUnit } = useI18n();
    const { t } = useTranslation();
    const form = useForm<BlockForm>({
        resolver: yupResolver(schema),
        defaultValues: {
            userIsSentinel: false,
            canary_offset: -30,
            reference_offset: 60,
            orientation: "heading",
            ab_line: null,
            heading: 0,
            implement_width_feet: 40,
            pass_count: 3,
            length_feet: 240,
            pair_layout: "side-by-side",
        },
        mode: "onChange",
    });
    const [
        abLine,
        implementWidth,
        passCount,
        plotLength,
        heading,
        orientation,
        pairLayout,
        canaryOffset,
        referenceOffset,
    ] = form.watch([
        "ab_line",
        "implement_width_feet",
        "pass_count",
        "length_feet",
        "heading",
        "orientation",
        "pair_layout",
        "canary_offset",
        "reference_offset",
    ]);

    const { fieldQuery, geometryQuery } = useFieldDashboard() ?? {};
    const { map, mapLoaded } = useMap();
    const { userIsSentinel, user } = useAuth();
    const { fullPlotOffsetEnabled } = useFeatureFlags();
    const units = useMemo(() => {
        return user?.unit_preference === "metric" ? "meters" : "feet";
    }, [user?.unit_preference]);
    const fieldBoundary = useMemo(() => {
        return geometryQuery?.data?.field;
    }, [geometryQuery?.data?.field]);

    const { useGetRequest, useMutateRequest } = useRequest();
    const getABLine = useGetRequest<AbLine[]>({
        url: `/fields/${fieldId}/ab_lines/`,
        enabled: !!fieldId,
    });
    const getGrid = useGetRequest<MultiLineString>({
        url: `ab_lines/${abLine}/grid/`,
        enabled: !!abLine,
        params: {
            pass_spacing: implementWidth,
            passes: passCount,
        },
    });
    const addAbLine = useMutateRequest<AbLine>({
        url: "/ab_lines/upload/",
        method: "post",
        successMessage: t("success.ab_line_uploaded", { ns: "messages" }),
        errorMessage: t("error.error_uploading_ab_line", { ns: "messages" }),
        onSuccess: (data) => {
            getABLine.refetch();
            form.setValue("ab_line", data.id);
        },
    });
    useEffect(() => {
        if (!map || !mapLoaded) return;
        const selectedABLine = getABLine.data?.find(
            (line) => line.id === Number(abLine),
        );
        if (selectedABLine) {
            form.setValue(
                "heading",
                turf.bearingToAzimuth(
                    turf.bearing(
                        turf.point(selectedABLine.geometry.coordinates[0]),
                        turf.point(selectedABLine.geometry.coordinates[1]),
                    ),
                ),
            );
        }
        addUpdateGeojsonSource(map, AB_LINE_SOURCE, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: selectedABLine
                    ? [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: selectedABLine.geometry,
                        },
                    ]
                    : [],
            },
            tolerance: 0,
        });
        addUpdateGeojsonSource(map, PLOT_GRID_SOURCE, {
            type: "geojson",
            data: getGrid.data || {
                type: "FeatureCollection",
                features: [],
            },
            tolerance: 0,
        });
        addUpdateGeojsonLayers(map, [
            AB_LINE_LAYER,
            AB_POINTS_LAYER,
            PLOT_GRID_LAYER,
        ]);
        updateLayerVisibility(
            map,
            [AB_LINE_LAYER.id, AB_POINTS_LAYER.id, PLOT_GRID_LAYER.id],
            true,
        );
        moveLayersToTop(map, [
            AB_LINE_LAYER.id,
            AB_POINTS_LAYER.id,
            PLOT_GRID_LAYER.id,
        ]);

        return () => {
            removeLayersIfExists(map, [
                AB_LINE_LAYER.id,
                PLOT_GRID_LAYER.id,
                AB_POINTS_LAYER.id,
            ]);
            removeSourcesIfExists(map, [AB_LINE_SOURCE, PLOT_GRID_SOURCE]);
        };
    }, [map, mapLoaded, getABLine.data, abLine, getGrid.data, form]);

    const [blockCenter, setBlockCenter] = useState<number[] | null>(null);
    const [firstPlotType, setFirstPlotType] = useState<PlotType>(
        PlotType.Canary,
    );

    useEffect(() => {
        if (plots && plots.length > 0) {
            const canary = plots.find(
                (plot) => plot.properties?.plot_type === PlotType.Canary,
            );
            const reference = plots.find(
                (plot) => plot.properties?.plot_type === PlotType.Reference,
            );
            if (!canary || !reference) return;
            form.reset({
                userIsSentinel: userIsSentinel || fullPlotOffsetEnabled,
                canary_offset: canary.properties?.rate_offset,
                reference_offset: reference.properties?.rate_offset,
                ab_line: reference.properties?.ab_line,
                orientation: !canary.properties?.ab_line
                    ? "heading"
                    : "ab-line",
                heading: getBlockHeading(canary),
                implement_width_feet: canary.properties?.implement_width_feet,
                pass_count: canary.properties?.pass_count,
                length_feet: canary.properties?.length_feet,
                canary_id: Number(canary.id),
                reference_id: Number(reference.id),
                canary_geometry: canary.geometry,
                reference_geometry: reference.geometry,
            });
            setBlockCenter(
                (canary.geometry as MultiPolygon).coordinates[0][0][0],
            );
        } else if (fieldQuery?.data?.center) {
            form.setValue(
                "userIsSentinel",
                userIsSentinel || fullPlotOffsetEnabled,
                {
                    shouldValidate: true,
                },
            );
            setBlockCenter(fieldQuery.data.center);
        }
    }, [plots, fieldQuery?.data, form, userIsSentinel, fullPlotOffsetEnabled]);

    const [dragActive, setDragActive] = useState<boolean>(false);
    const startDrag = useCallback(
        (e: MapMouseEvent) => {
            if (!map || !!dragActive) return;
            setDragActive(true);
            setBlockCenter(e.lngLat.toArray());
            map.getCanvas().style.cursor = "grabbing";
            map.dragPan.disable();
        },
        [map, dragActive],
    );
    const drag = useCallback(
        (e: MapMouseEvent) => {
            if (!map || !dragActive) return;
            const coords = e.lngLat.toArray();
            const mousePoint = turf.point(coords);
            if (fieldBoundary && !turf.booleanWithin(mousePoint, fieldBoundary))
                return;
            setBlockCenter(mousePoint.geometry.coordinates);
        },
        [map, dragActive, fieldBoundary],
    );
    const stopDrag = useCallback(() => {
        if (!map || !dragActive) return;
        setDragActive(false);
        map.getCanvas().style.cursor = "";
        map.dragPan.enable();
    }, [map, dragActive]);

    const makeBlockGeometry = useCallback(
        (
            startPoint: number[],
            width: number,
            length: number,
            heading: number,
            grid?: MultiLineString,
        ): Feature[] => {
            const widthKm = turf.convertLength(width, units, "kilometers"); // NEEDS UNITS
            const lengthKm = turf.convertLength(length, units, "kilometers"); // NEEDS UNITS
            const pointA = grid
                ? turf.nearestPointOnLine(grid, turf.point(startPoint)).geometry
                    .coordinates
                : startPoint;
            const pointB = turf.destination(pointA, lengthKm, heading, {
                units: "kilometers",
            });
            const pointC = turf.destination(pointB, widthKm, heading + 90, {
                units: "kilometers",
            });
            const pointD = turf.destination(pointA, widthKm, heading + 90, {
                units: "kilometers",
            });
            const plot1 = turf.multiPolygon([
                [
                    [
                        turf.getCoords(pointA),
                        turf.getCoords(pointB),
                        turf.getCoords(pointC),
                        turf.getCoords(pointD),
                        turf.getCoords(pointA),
                    ],
                ],
            ]);
            const pointE =
                pairLayout === "side-by-side"
                    ? turf.destination(pointA, widthKm, heading - 90, {
                        units: "kilometers",
                    })
                    : turf.destination(pointB, lengthKm, heading, {
                        units: "kilometers",
                    });
            const pointF =
                pairLayout === "side-by-side"
                    ? turf.destination(pointE, lengthKm, heading, {
                        units: "kilometers",
                    })
                    : turf.destination(pointE, widthKm, heading + 90, {
                        units: "kilometers",
                    });
            const plot2 =
                pairLayout === "side-by-side"
                    ? turf.multiPolygon([
                        [
                            [
                                turf.getCoords(pointA),
                                turf.getCoords(pointE),
                                turf.getCoords(pointF),
                                turf.getCoords(pointB),
                                turf.getCoords(pointA),
                            ],
                        ],
                    ])
                    : turf.multiPolygon([
                        [
                            [
                                turf.getCoords(pointB),
                                turf.getCoords(pointE),
                                turf.getCoords(pointF),
                                turf.getCoords(pointC),
                                turf.getCoords(pointB),
                            ],
                        ],
                    ]);
            plot1.properties = {
                plot_type: firstPlotType,
            };
            plot2.properties = {
                plot_type:
                    firstPlotType === PlotType.Canary
                        ? PlotType.Reference
                        : PlotType.Canary,
            };
            return [plot1, plot2];
        },
        [pairLayout, firstPlotType, units],
    );

    useEffect(() => {
        if (map && mapLoaded && blockCenter) {
            const blocks = makeBlockGeometry(
                blockCenter,
                implementWidth * passCount,
                plotLength,
                Number(heading),
                orientation === "ab-line" ? getGrid.data : undefined,
            );
            form.setValue(
                "canary_geometry",
                firstPlotType === PlotType.Canary
                    ? blocks[0].geometry
                    : blocks[1].geometry,
            );
            form.setValue(
                "reference_geometry",
                firstPlotType === PlotType.Canary
                    ? blocks[1].geometry
                    : blocks[0].geometry,
            );
            addUpdateGeojsonSource(map, PLOT_HIGHLIGHT_SOURCE, {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: blocks,
                },
            });
            addUpdateGeojsonLayers(map, [
                PLOT_HIGHLIGHT_FILL_LAYER,
                PLOT_HIGHLIGHT_LINE_LAYER,
            ]);
            moveLayersToTop(map, [
                PLOT_HIGHLIGHT_FILL_LAYER.id,
                PLOT_HIGHLIGHT_LINE_LAYER.id,
            ]);
            if (placement === "manual") {
                map.on("mousedown", PLOT_HIGHLIGHT_FILL_LAYER.id, startDrag);
                map.on("mousemove", drag);
                map.on("mouseup", stopDrag);
            }
        }
        if (onFormChange) {
            const formData =
                placement === "automatic"
                    ? form.getValues()
                    : [
                        {
                            id: form.getValues("canary_id"),
                            geometry: form.getValues("canary_geometry"),
                            properties: {
                                ab_line: abLine,
                                plot_type: PlotType.Canary,
                                rate_offset: canaryOffset,
                                implement_width_feet: implementWidth,
                                pass_count: passCount,
                                length_feet: plotLength,
                            },
                        },
                        {
                            id: form.getValues("reference_id"),
                            geometry: form.getValues("reference_geometry"),
                            properties: {
                                ab_line: abLine,
                                plot_type: PlotType.Reference,
                                rate_offset: referenceOffset,
                                implement_width_feet: implementWidth,
                                pass_count: passCount,
                                length_feet: plotLength,
                            },
                        },
                    ];
            onFormChange(formData);
        }
    }, [
        map,
        mapLoaded,
        blockCenter,
        abLine,
        plotLength,
        heading,
        implementWidth,
        passCount,
        pairLayout,
        firstPlotType,
        canaryOffset,
        referenceOffset,
        getGrid.data,
        orientation,
        placement,
        form,
        startDrag,
        makeBlockGeometry,
        stopDrag,
        drag,
        onFormChange,
        form.formState.isValid,
    ]);
    useEffect(() => {
        return () => {
            if (!map) return;
            removeLayersIfExists(map, [
                PLOT_HIGHLIGHT_FILL_LAYER.id,
                PLOT_HIGHLIGHT_LINE_LAYER.id,
            ]);
            removeSourcesIfExists(map, [PLOT_HIGHLIGHT_SOURCE]);
            if (placement === "manual") {
                map.off("mousedown", PLOT_HIGHLIGHT_FILL_LAYER.id, startDrag);
                map.off("mousemove", drag);
                map.off("mouseup", stopDrag);
            }
        };
    }, [map, placement, startDrag, drag, stopDrag]);

    return (
        <FormProvider {...form}>
            <form className="flex flex-col w-full gap-2 text-sm">
                <div className="flex flex-row w-full gap-2 justify-between items-end px-2">
                    <SelectButtonInput
                        name="orientation"
                        options={[
                            {
                                id: "heading",
                                description: t("heading", { ns: "rx" }),
                            },
                            {
                                id: "ab-line",
                                description: t("ab_line", { ns: "rx" }),
                            },
                        ]}
                        orientation="horizontal"
                        label={t("orientation", { ns: "rx" })}
                        isRequired
                        size="sm"
                    />
                    <div className="flex flex-col gap-2">
                        {orientation === "ab-line" ? (
                            <div className="flex flex-row w-full gap-2 items-end">
                                <AutoCompleteInput
                                    name="ab_line"
                                    label={t("ab_line", { ns: "rx" })}
                                    labelPlacement="outside"
                                    placeholder={t("select_ab_line", {
                                        ns: "rx",
                                    })}
                                    isRequired
                                    options={
                                        !getABLine.data
                                            ? []
                                            : getABLine.data?.map((line) => ({
                                                id: line.id,
                                                description:
                                                    line.name ||
                                                    "Unnamed AB Line",
                                            }))
                                    }
                                />
                                <FileInput
                                    label={t("upload", { ns: "rx" })}
                                    accept=".geojson,.json,.zip"
                                    isLoading={addAbLine.isPending}
                                    onSelect={(file) => {
                                        if (file instanceof File) {
                                            const formData = new FormData();
                                            formData.append("file", file);
                                            formData.append(
                                                "field",
                                                fieldId.toString(),
                                            );
                                            addAbLine.mutate({
                                                data: formData,
                                            });
                                        }
                                    }}
                                />
                            </div>
                        ) : (
                            <NumberInput
                                name="heading"
                                label={t("heading", { ns: "rx" })}
                                labelPlacement="outside"
                                isRequired
                                min={0}
                                max={360}
                                endContent={
                                    <div className="flex flex-row gap-1">
                                        <Button
                                            isIconOnly
                                            size="sm"
                                            variant="flat"
                                            color="primary"
                                            onPress={() => {
                                                const currentValue = heading;
                                                if (currentValue !== null) {
                                                    const newValue =
                                                        (currentValue - 5) %
                                                        360;
                                                    form.setValue(
                                                        "heading",
                                                        newValue < 0
                                                            ? newValue + 360
                                                            : newValue,
                                                    );
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faRotateLeft}
                                            />
                                        </Button>
                                        <Button
                                            isIconOnly
                                            size="sm"
                                            variant="flat"
                                            color="primary"
                                            onPress={() => {
                                                const currentValue = heading;
                                                if (currentValue !== null) {
                                                    form.setValue(
                                                        "heading",
                                                        (currentValue + 5) %
                                                        360,
                                                    );
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faRotateRight}
                                            />
                                        </Button>
                                    </div>
                                }
                            />
                        )}
                    </div>
                </div>
                <div className="flex flex-row w-full gap-4 justify-between items-end px-2">
                    <NumberInput
                        name="implement_width_feet"
                        label={t("pass_width", { ns: "rx" })}
                        labelPlacement="outside"
                        isRequired
                        type="number"
                        min={0}
                        endContent={
                            <span className="text-sm">
                                {formatUnit("ft", "m")}
                            </span>
                        }
                    />
                    <NumberInput
                        name="pass_count"
                        label={t("passes", { ns: "rx" })}
                        labelPlacement="outside"
                        isRequired
                        type="number"
                        min={1}
                        endContent={<span className="text-sm">passes</span>}
                    />
                    <NumberInput
                        name="length_feet"
                        label={t("length", { ns: "rx" })}
                        labelPlacement="outside"
                        isRequired
                        type="number"
                        min={0}
                        endContent={
                            <span className="text-sm">
                                {formatUnit("ft", "m")}
                            </span>
                        }
                    />
                </div>
                <div className="flex flex-row w-full gap-2 justify-between items-end px-2">
                    <div className="flex flex-col w-full">
                        <SelectButtonInput
                            name="pair_layout"
                            options={[
                                {
                                    id: "side-by-side",
                                    description: "Side by Side",
                                    icon: (
                                        <FontAwesomeIcon
                                            icon={faArrowsLeftRight}
                                        />
                                    ),
                                },
                                {
                                    id: "end-to-end",
                                    description: "End to End",
                                    icon: (
                                        <FontAwesomeIcon
                                            icon={faArrowsUpDown}
                                        />
                                    ),
                                },
                            ]}
                            orientation="horizontal"
                            label={t("pair_layout", { ns: "rx" })}
                            isRequired
                            size="sm"
                        />
                    </div>
                    <div className="flex flex-col gap-2 px-2">
                        <label>{t("plot_location", { ns: "rx" })}</label>
                        <Button
                            variant="flat"
                            color="primary"
                            onPress={() => {
                                setFirstPlotType((prev) =>
                                    prev === PlotType.Canary
                                        ? PlotType.Reference
                                        : PlotType.Canary,
                                );
                            }}
                        >
                            <FontAwesomeIcon icon={faShuffle} />
                            {t("swap", { ns: "rx" })}
                        </Button>
                    </div>
                </div>
                <div className="flex flex-row w-full gap-2 justify-between items-end px-2">
                    <NumberInput
                        name="canary_offset"
                        label={t("canary_offset", { ns: "rx" })}
                        labelPlacement="outside"
                        isRequired
                        type="number"
                        min={-90}
                        max={0}
                        color="danger"
                    />
                    <NumberInput
                        name="reference_offset"
                        label={t("reference_offset", { ns: "rx" })}
                        labelPlacement="outside"
                        isRequired
                        type="number"
                        min={0}
                        max={90}
                        color="secondary"
                    />
                </div>
            </form>
        </FormProvider>
    );
}

export default BlockPlotForm;
